/**
 * Requester search-or-create — type a name or email, pick a match, or take the
 * "new requester" branch which pops the name field. Search is server-side
 * (name OR email, org-scoped, 8 results); the directory itself will grow via
 * Microsoft auto-provisioning (CHANGELOG Step 39), at which point this picker
 * simply starts finding people who never contacted support before.
 */
import { useEffect, useRef, useState } from 'react';
import { CircleUserRound, Plus } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import type { Person } from './api';

export interface RequesterValue {
  email: string;
  name: string;
  isNew: boolean;
}

export function RequesterPicker({
  value,
  onChange,
}: {
  value: RequesterValue;
  onChange: (v: RequesterValue) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Person[]>([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const res = await apiFetch<{ entries: Person[] }>(
          `/api/org/people?q=${encodeURIComponent(query.trim())}`,
        );
        setResults(res.entries);
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query]);

  const looksLikeEmail = /\S+@\S+\.\S+/.test(query.trim());

  if (value.email) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
        <span className="flex min-w-0 items-center gap-2 text-sm">
          <CircleUserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {value.name ? `${value.name} · ` : ''}
            {value.email}
          </span>
          {value.isNew && (
            <span className="shrink-0 rounded bg-brand/15 px-1.5 text-[10px] font-medium text-brand">
              new
            </span>
          )}
        </span>
        <button
          type="button"
          className="text-xs text-muted-foreground underline hover:text-foreground"
          onClick={() => {
            onChange({ email: '', name: '', isNew: false });
            setQuery('');
          }}
        >
          change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150) /* let the option's click land first */}
        placeholder="Search by name or email…"
      />
      {open && (results.length > 0 || looksLikeEmail) && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
              onClick={() => onChange({ email: p.email, name: p.name ?? '', isNew: false })}
            >
              <CircleUserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{p.name ?? p.email}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{p.name ? p.email : p.kind}</span>
            </button>
          ))}
          {looksLikeEmail && !results.some((r) => r.email === query.trim().toLowerCase()) && (
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                results.length > 0 && 'border-t',
              )}
              onClick={() => onChange({ email: query.trim().toLowerCase(), name: '', isNew: true })}
            >
              <Plus className="h-4 w-4 shrink-0 text-brand" />
              <span>
                New requester <span className="text-muted-foreground">{query.trim().toLowerCase()}</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
